import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { Button, Card, Col, Container, Modal, Row, Form } from "react-bootstrap"
import { getCategories, addCategory, editCategory, deleteCategory } from "../../actions/others/categoriesActions"
import { RootState, AppDispatch } from "../../store"
import { useAuth } from "../../components/auth/useAuth"
import { CustomDataTable } from "../../components/design/Datatables/CustomDataTable"
import Swal from "sweetalert2"
import { FormMode } from "../../types/common"
import Loader from "../../components/loaders/Loader"
import { Pencil } from "lucide-react"

export interface Category {
    _id?: string
    name?: string
}

export default function Categories() {
    const { t } = useTranslation()
    const { clientauth } = useAuth()
    const dispatch: AppDispatch = useDispatch()

    const [page, setPage] = useState<number>(1)
    const [limit, setLimit] = useState<number>(25)
    const [search, setSearch] = useState<string>("")
    const [searchAux, setSearchAux] = useState('')
    const [totalRows, setTotalRows] = useState<number>(0)

    const { category, categories, resultcategory, total } = useSelector((state: RootState) => state.categories)

    const [modalShow, setModalShow] = useState<boolean>(false)
    const [formState, setFormState] = useState<Category>({})
    const [isAddingOrEditing, setIsAddingOrEditing] = useState<FormMode>(null)
    const [isRegistering, setIsRegistering] = useState<boolean>(false)

    useEffect(() => {
        if (clientauth) {
            dispatch(getCategories(page, limit, search) as any)
        }
    }, [dispatch, t, clientauth])

    useEffect(() => {
        if (clientauth) {
            dispatch(getCategories(page, limit, search) as any)
        }
    }, [page, limit, search])

    useEffect(() => {
        setTotalRows(total || 0)
    }, [total])

    useEffect(() => {
        if (resultcategory) {
            setModalShow(false)
            setIsRegistering(false)
        }
    }, [resultcategory])

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const target = e.target as HTMLInputElement | HTMLSelectElement
        const { name, value, type } = target
        const inputValue = type === "checkbox" ? (target as HTMLInputElement).checked : value

        setFormState({
            ...formState,
            [name]: inputValue,
        })
    }

    const openModal = () => {
        setIsAddingOrEditing("adding")
        setFormState({})
        setModalShow(true)
    }

    const selectCategory = (category: Category) => {
        setFormState({ ...category })
        setIsAddingOrEditing("editing")
        setModalShow(true)
    }

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        setIsRegistering(true)

        if (isAddingOrEditing === "editing") {
            if (formState._id) {
                dispatch(editCategory(formState, formState._id) as any)
            }
        } else {
            dispatch(addCategory(formState) as any)
        }

        setIsRegistering(false)
    }

    const handleRemoveCategory = (category: Category) => {
        const categoryId = category._id
        if (!categoryId) return

        Swal.fire({
            showConfirmButton: true,
            showDenyButton: true,
            confirmButtonText: t('global.delete'),
            denyButtonText: t('global.cancel'),
            icon: 'info',
            text: t('categoryTranslate.removeQuestion', { name: category.name })
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(deleteCategory(categoryId))
            }
        })
    }

    useEffect(() => {
        if (resultcategory) {
            let resultcategorynow = resultcategory
            dispatch({ type: 'CATEGORY_RESET_RESULT' })
            switch (resultcategorynow) {
                case 'success':
                    Swal.fire({
                        showConfirmButton: true,
                        showDenyButton: false,
                        confirmButtonText: t('global.great'),
                        icon: 'success',
                        text: t('categoryTranslate.addsuccess')
                    }).then(() => {
                        setModalShow(false)
                        setIsAddingOrEditing(false)
                    })
                    break;
                case 'needMoreData':
                    Swal.fire({
                        showConfirmButton: true,
                        showDenyButton: false,
                        confirmButtonText: t('global.ok'),
                        icon: 'warning',
                        text: t('categoryTranslate.needMoreData')
                    })
                    break;
                case 'editSuccess':
                    Swal.fire({
                        showConfirmButton: true,
                        showDenyButton: false,
                        confirmButtonText: t('global.great'),
                        icon: 'success',
                        text: t('categoryTranslate.editSuccess')
                    }).then(() => {
                        setModalShow(false)
                        setIsAddingOrEditing(false)
                    })
                    break;
                case 'categoryInUse':
                    Swal.fire({
                        showConfirmButton: true,
                        showDenyButton: false,
                        confirmButtonText: t('global.ok'),
                        icon: 'warning',
                        text: t('categoryTranslate.categoryInUse')
                    })
                    break;
                case 'deleteSuccess':
                    Swal.fire({
                        showConfirmButton: true,
                        showDenyButton: false,
                        confirmButtonText: t('global.great'),
                        icon: 'success',
                        text: t('categoryTranslate.deleteSuccess')
                    })
                    break;
                default:
                    break;
            }
        }
        // eslint-disable-next-line
    }, [resultcategory])

    const columnsCategory = [
        {
            name: t("categoryTranslate.name"),
            cell: (category: Category) => (
                <div className="cursor" onClick={() => selectCategory(category)}>
                    {category.name}
                </div>
            ),
            selector: (category: Category) => category.name || "",
            sortable: true,
        }, {
            name: "",
            width: "100px",
            cell: (category: Category) => (
                <div className="cursor" onClick={() => selectCategory(category)}>
                    <Pencil size={16} />
                </div>
            ),
        }, {
            name: '',
            width: '100px',
            cell: (category: Category) => (
                <i className='fas fa-trash cursor' onClick={() => handleRemoveCategory(category)}></i>
            ),
            sortable: false
        }
    ]

    return (
        <Container className='p-1 py-5 p-md-5'>
            <Row>
                <Col xs={12}>
                    <h1>{t('menus.categories')}</h1>
                </Col>
                <Col xs={12} className='d-flex justify-content-end'>
                    <button className="btn btn-primary ms-2" onClick={openModal}>
                        {t("categoryTranslate.addCategory")}
                    </button>
                </Col>
            </Row>
            <Row>
                <Col xs={12} className='my-3'>
                    <Row>
                        <Col>
                            <Card>
                                <Card.Body>
                                    <Row className="justify-content-end">
                                        <Col xs={12} lg={4} className="d-flex">
                                            <input
                                                type="text"
                                                className="form-control me-2 input-search"
                                                placeholder={t("datatables.buscar")}
                                                value={searchAux || ""}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                    setSearchAux(e.target.value);
                                                    setPage(1);
                                                }}
                                                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                                    if (e.key === "Enter") {
                                                        setSearch(searchAux);
                                                        setPage(1);
                                                    }
                                                }}
                                            />
                                            <Button
                                                className="button-search"
                                                onClick={() => setSearch(searchAux)}
                                            >
                                                <i className="fas fa-search" />
                                            </Button>
                                        </Col>
                                    </Row>

                                    {categories?.length > 0 ? (
                                        <CustomDataTable
                                            columns={columnsCategory}
                                            data={categories}
                                            pagination
                                            paginationServer
                                            paginationTotalRows={totalRows}
                                            searchText={search}
                                            onChangeRowsPerPage={(newLimit: number) => {
                                                setLimit(newLimit);
                                                setPage(1);
                                            }}
                                            onChangePage={(newPage: number) => {
                                                setPage(newPage);
                                            }}
                                        />
                                    ) : (
                                        <h4 className="mt-4">{t("categoryTranslate.thereisnotcategory")}</h4>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Col>
            </Row>
            <Modal
                show={modalShow}
                onHide={() => setModalShow(false)}
                aria-labelledby="contained-modal-title-vcenter"
                size="lg"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title id="contained-modal-title-vcenter">
                        <h2>{t('categoryTranslate.categoryEdit')}</h2>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleSubmit}>
                        <Row className="mb-3">
                            <Col xs={12} md={6}>
                                <Form.Group controlId="formCategoryName">
                                    <Form.Label>{t("categoryTranslate.name")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="name"
                                        value={formState.name || ""}
                                        onChange={handleInputChange as any}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col xs={12} lg={6} className="my-2 ms-auto">
                                <Button variant='primary' type="submit" className="w-100 my-2">
                                    {isRegistering ?
                                        <>
                                            <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                                        </>
                                        :
                                        isAddingOrEditing === 'editing' ?
                                            <>{t('global.edit')}</>
                                            :
                                            <>{t('global.register')}</>
                                    }
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                </Modal.Body>
            </Modal>
        </Container>
    )
}
